import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { humanizeChartSpec, normalizeChartSpec, parseChartJson } from './formatters';

let plotlyPromise;

function loadPlotly() {
    if (!plotlyPromise) {
        plotlyPromise = import(
            /* webpackChunkName: "plotly" */
            'plotly.js-dist-min'
        ).then(module => module.default || module);
    }
    return plotlyPromise;
}

const ChatChart = ({ chartJson }) => {
    const chartRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [renderError, setRenderError] = useState(null);

    const parsed = useMemo(() => {
        try {
            return {
                value: humanizeChartSpec(normalizeChartSpec(parseChartJson(chartJson))),
                error: null,
            };
        } catch (error) {
            return { value: null, error };
        }
    }, [chartJson]);

    useEffect(() => {
        if (parsed.error || !parsed.value || !chartRef.current) {
            setLoading(false);
            return undefined;
        }

        let active = true;
        let Plotly;
        let resizeObserver;
        const element = chartRef.current;

        setLoading(true);
        setRenderError(null);

        loadPlotly()
            .then(engine => {
                if (!active) return;
                Plotly = engine;
                const layout = Object.assign({}, parsed.value.layout || {}, {
                    autosize: true,
                    margin: Object.assign(
                        { l: 48, r: 20, t: 48, b: 48 },
                        (parsed.value.layout && parsed.value.layout.margin) || {}
                    ),
                });

                return Plotly.newPlot(
                    element,
                    Array.isArray(parsed.value.data) ? parsed.value.data : [],
                    layout,
                    { responsive: true, displayModeBar: 'hover', displaylogo: false }
                ).then(() => {
                    if (!active) return;
                    setLoading(false);
                    if (typeof ResizeObserver !== 'undefined') {
                        resizeObserver = new ResizeObserver(() => {
                            if (active && Plotly && Plotly.Plots) {
                                Plotly.Plots.resize(element);
                            }
                        });
                        resizeObserver.observe(element);
                    }
                });
            })
            .catch(error => {
                if (active) {
                    setRenderError(error);
                    setLoading(false);
                }
            });

        return () => {
            active = false;
            if (resizeObserver) resizeObserver.disconnect();
            if (Plotly && element) Plotly.purge(element);
        };
    }, [parsed]);

    if (parsed.error || renderError) {
        return (
            <section className="ai-chat-section ai-chat-chart-fallback">
                <strong>Chart could not be displayed</strong>
                <details>
                    <summary>View raw chart data</summary>
                    <pre>{chartJson}</pre>
                </details>
            </section>
        );
    }

    return (
        <section className="ai-chat-section ai-chat-chart">
            {loading && (
                <div className="ai-chat-chart-loading">
                    <Spinner animation="border" size="sm" />
                    <span>Loading chart…</span>
                </div>
            )}
            <div ref={chartRef} className="ai-chat-chart-canvas" />
        </section>
    );
};

export default ChatChart;
